/**
 * useCampaignVideoDeepLink — URL → local state reader for the live-video
 * popup hosted on the Confirmed / Live Content / Reporting tabs.
 *
 *   ?panel=video&video=<roster_entry_id>  → returns the matching row from the
 *                                            supplied list, or null
 *
 * The caller wires a useEffect that mirrors the matched row into its local
 * popup state (with an open-once guard so closing doesn't immediately reopen
 * on re-render). Same pattern as useCampaignReviewDeepLink — the reader is
 * a pure resolver; effects live at the call site.
 */
import { useSearch } from '@tanstack/react-router';
import type { CampaignSearch } from './search';

export function useCampaignVideoDeepLink<T>(
  rows: ReadonlyArray<T>,
  getId: (row: T) => string,
): { videoId: string | null; row: T | null } {
  const { panel, video } = useSearch({ strict: false }) as Partial<CampaignSearch>;
  if (panel !== 'video' || !video) return { videoId: null, row: null };

  const row = rows.find((r) => getId(r) === video);
  return { videoId: video, row: row ?? null };
}
